import { ApiConstant, AppConstant, KeyConstant, SystemConstant } from "const";
import { all, call, put, select } from "redux-saga/effects";
import store, { SystemActions } from "redux-store";
import CallingAction, { CallingActions } from "redux-store/calling.redux";
import ConversationAction from "redux-store/conversation.redux";
import { remoteApiFactory } from "services";
import { getInteractor } from "services/local.service";
import { deleteMessageAfterDeleteCallingHistory } from "services/message.service";
import { getFileFromTus } from "services/multiple-file";
import { AttachmentUtil, isNotEqual, StorageUtil, toCamel, toSnake } from "utils";
import { getBranches } from "./branch.saga";

const callingUtil = window.electronLibs.ringRtc.callingUtil;
const socket = window.electronLibs.socket;

export function* checkCallingStatus(action) {
  try {
    const { data } = action;
    const { accountId: calleeAccountId } = data;
    const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();
    const branchInfo = StorageUtil.getItem(KeyConstant.KEY_BRANCH_INFO, prefixKey) || {};
    const [callerAccountId, branchId] = [branchInfo.accountId, branchInfo.id];

    const {
      callingRedux: { callingGroupDetail, isReceiver, isOpenCallingDialog },
    } = yield select();

    const [callerResp, calleeResp] = yield all([
      call(remoteApiFactory.getBranchApi(prefixKey).getCallingStatus, {
        account_id: callerAccountId,
      }),
      call(remoteApiFactory.getBranchApi(prefixKey).getCallingStatus, {
        account_id: calleeAccountId,
      }),
    ]);

    let isInAnotherCall = false;
    let isCalleeInAnotherCall = false;

    if (callerResp.status === ApiConstant.STT_OK) {
      const callerData = toCamel(callerResp.data);
      isInAnotherCall =
        callerData.callingF === SystemConstant.CALL_CHECK.inCall ||
        callerData.blockF === SystemConstant.ACCOUNT_CHECK.blocked;
      yield put(CallingAction.callingSet({ isInAnotherCall }));
    }

    if (calleeResp.status === ApiConstant.STT_OK) {
      const calleeData = toCamel(calleeResp.data);
      isCalleeInAnotherCall =
        calleeData.callingF === SystemConstant.CALL_CHECK.inCall ||
        calleeData.blockF === SystemConstant.ACCOUNT_CHECK.blocking;
      yield put(CallingAction.callingSet({ isCalleeInAnotherCall, isPingToCallee: true }));
    }

    if (false === isCalleeInAnotherCall && false === isInAnotherCall && false === Boolean(data.isEndCall)) {
      yield getBranches(prefixKey);
      if (callingGroupDetail?.roomId) {
        const dataToToken = {
          branch_id: branchId,
          room_id: callingGroupDetail?.roomId,
          is_moderator: !Boolean(isReceiver),
        };
        const callingTokenRes = yield call(remoteApiFactory.getBranchApi(prefixKey).postMeetToken, dataToToken);
        if (callingTokenRes.status === ApiConstant.STT_OK) {
          const callInfo = toCamel(callingTokenRes.data);
          StorageUtil.setItem(KeyConstant.KEY_MEET_OBJ, callInfo, prefixKey);
          yield put(
            CallingAction.callingSet({
              isSendMeetInfoSuccess: true,
            }),
          );
        } else {
          yield put(
            CallingAction.callingSet({
              isSendMeetInfoSuccess: false,
            }),
          );
        }
      }
    } else if (data.isEndCall) {
      StorageUtil.setItem(KeyConstant.KEY_MEET_OBJ, null, prefixKey);
    }

    if (isOpenCallingDialog === AppConstant.CALLING_STATUS.checking) {
      yield put(
        CallingAction.callingSet({
          isOpenCallingDialog: AppConstant.CALLING_STATUS.finishChecking,
        }),
      );
    }

    yield put(CallingAction.callingSet({ isFetching: false }));
  } catch (error) {
    yield put(CallingAction.callingSet({ error: error, isFetching: false }));
  }
}

export function* checkReceiversStatus(action) {
  const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();
  const callingGroupDetail = action.callingGroupDetail;

  try {
    yield getBranches(prefixKey);
    const branchInfo = StorageUtil.getItem(KeyConstant.KEY_BRANCH_INFO, prefixKey) || {};
    const calleeAccountId = branchInfo.accountId;

    const {
      callingRedux: { isOpenCallingDialog },
    } = yield select();

    const [calleeResp] = yield all([
      call(remoteApiFactory.getBranchApi(prefixKey).getCallingStatus, {
        account_id: calleeAccountId,
      }),
    ]);

    if (calleeResp.status === ApiConstant.STT_OK) {
      let data = toCamel(calleeResp.data);
      let isCalleeInAnotherCall = data.callingF === SystemConstant.CALL_CHECK.inCall;
      yield put(
        CallingAction.callingSet({
          isCalleeInAnotherCall,
          isPingToCallee: true,
          isReceiving: true,
        }),
      );

      if (false === Boolean(isCalleeInAnotherCall)) {
        if (callingGroupDetail?.roomId) {
          const dataToToken = {
            branch_id: branchInfo.id,
            room_id: callingGroupDetail?.roomId,
            is_moderator: false,
          };

          const callingTokenRes = yield call(remoteApiFactory.getBranchApi(prefixKey).postMeetToken, dataToToken);
          if (callingTokenRes.status === ApiConstant.STT_OK) {
            const callInfo = toCamel(callingTokenRes.data);
            StorageUtil.setItem(KeyConstant.KEY_MEET_OBJ, callInfo, prefixKey);
            yield put(
              CallingAction.callingSet({
                isSendMeetInfoSuccess: true,
              }),
            );
          } else {
            yield put(
              CallingAction.callingSet({
                isSendMeetInfoSuccess: false,
              }),
            );
          }
        }
      }
    }

    if (isOpenCallingDialog === AppConstant.CALLING_STATUS.checking) {
      yield put(
        CallingAction.callingSet({
          isOpenCallingDialog: AppConstant.CALLING_STATUS.finishChecking,
        }),
      );
    }

    yield put(CallingAction.callingSet({ isFetching: false }));
  } catch (error) {
    yield put(CallingAction.callingSet({ error: error, isFetching: false }));
  }
}

export const getCallHistory = async action => {
  const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);

  try {
    let sinceTime = 0;
    const callingGroupDetail = store.getState().callingRedux.callingGroupDetail;
    const callingGroupDetailRing = store.getState().callingRedux.callingGroupDetailRing;

    try {
      let loginTime = StorageUtil.getItem(KeyConstant.KEY_LOGIN_TIME, prefixKey) || 0;
      let lastRecord = await getInteractor(prefixKey).LocalCallHistoryService.getLastNotByMe(branchId, accountId);

      sinceTime = lastRecord?.created ? lastRecord?.created + 1 : Number(loginTime);
    } catch (error) {
      console.error({ error });
    }
    const data = {
      sinceTime: sinceTime,
    };
    let response = await remoteApiFactory.getBranchApi(prefixKey).getCallHistory(toSnake(data));
    if (response.status === ApiConstant.STT_OK && response.data.data.length > 0) {
      let responseData = response.data.data;
      if (responseData.length > 0) {
        await getInteractor(prefixKey).LocalCallHistoryService.save(responseData);
        store.dispatch(
          CallingAction.callingSet({
            isFetchHistoryTimestamp: Date.now(),
          }),
        );
      }
      if (responseData.find(item => item.sender_id === accountId)) {
        store.dispatch(
          ConversationAction.conversationSet({
            isUpdateViewMode: Date.now(),
          }),
        );
      }

      let callHistory = responseData.find(item => item.room_id === callingGroupDetail?.roomId);

      // Check each instance in callingGroupDetailRing map for ended calls
      if (callingGroupDetailRing && typeof callingGroupDetailRing === "object") {
        for (const groupId in callingGroupDetailRing) {
          const groupDetail = callingGroupDetailRing[groupId];
          if (groupDetail?.roomId) {
            const callHistoryRing = responseData.find(item => item.room_id === groupDetail.roomId);
            // Nếu có cuộc gọi nhóm đang mở và trạng thái cuộc gọi khác calling (cuộc gọi đang diễn ra) thì tắt cuộc gọi đi
            if (callHistoryRing && callHistoryRing.status !== SystemConstant.CALL_HISTORY_STATUS.calling) {
              console.log("getCallHistory - end call", { groupId, groupDetail, callHistoryRing }); // eslint-disable-line no-console
              callingUtil.reset(callHistoryRing.room_id);
              socket.unsubscribe(callHistoryRing.room_id);
            }
          }
        }
      }

      console.log("getCallHistory", { callingGroupDetail, callingGroupDetailRing, callHistory }); // eslint-disable-line no-console
      if (callHistory) {
        let groupCallOptions = store.getState().callingRedux.groupCallOptions;
        let newOptions = JSON.parse(callHistory.options);
        if (isNotEqual(groupCallOptions, newOptions)) {
          store.dispatch(CallingAction.callingSet({ groupCallOptions: newOptions }));
        }
      }
    }
  } catch (error) {
    console.error(error);
  }
};

export function* updateCallHistory(action) {
  try {
    const { data } = action;
    const prefixKey = action?.prefixKey || StorageUtil.getCurrentPrefixKey();

    const isDeleteRequest = data.state === SystemConstant.STATE.inactive;
    const isReadRequest = data.action_status === SystemConstant.CALL_HISTORY_ACTION_STATUS.seen;

    const updateRoomIds = data.room_ids;
    let isUpdateSuccess = true;

    // Delete call history: Update state = inactive
    // Send request to server with chunk size = 20
    if (Array.isArray(updateRoomIds) && updateRoomIds.length > 0) {
      let offset = 0;
      while (offset < updateRoomIds.length) {
        const subUnreadRoomIds = updateRoomIds.slice(offset, offset + SystemConstant.REQUEST_CHUNK_SIZE);
        if (subUnreadRoomIds && subUnreadRoomIds.length > 0) {
          const data2Req = { ...data, room_ids: subUnreadRoomIds };
          const response = yield remoteApiFactory.getBranchApi(prefixKey).updateCallHistory(toSnake(data2Req));
          if (response && response.status === ApiConstant.STT_OK) {
            // Update local
            const dataUpdate = { ...data2Req };
            delete dataUpdate.room_ids;

            yield getInteractor(prefixKey).LocalCallHistoryService.update(
              { ...dataUpdate, modified: Date.now() },
              { room_id: subUnreadRoomIds },
            );

            // Update calling_message in message.table when delete calling history
            if (isDeleteRequest) {
              yield deleteMessageAfterDeleteCallingHistory({ prefixKey, room_ids: subUnreadRoomIds });
            }
          } else {
            isUpdateSuccess = false;
          }
        }
        offset += SystemConstant.REQUEST_CHUNK_SIZE;
      }
    } else {
      // Delete all, read all
      const response = yield remoteApiFactory.getBranchApi(prefixKey).updateCallHistory(toSnake(data));
      if (response && response.status === ApiConstant.STT_OK) {
        // Update local
        const updateData = { ...data };
        delete updateData.room_ids;
        delete updateData.select_all;
        let updateCondition = null;
        switch (true) {
          case isDeleteRequest:
            updateCondition = { state: SystemConstant.STATE.active };
            break;

          case isReadRequest:
            updateCondition = { active_status: SystemConstant.CALL_HISTORY_ACTION_STATUS.unread };
            break;

          default:
            break;
        }

        if (updateCondition)
          yield getInteractor(prefixKey).LocalCallHistoryService.update(
            { ...updateData, modified: Date.now() },
            updateCondition,
          );
      } else {
        isUpdateSuccess = false;
      }
    }

    // Trigger update UI
    if (isUpdateSuccess && isReadRequest) {
      yield put(
        SystemActions.systemSet({
          seenCallHistoryTimestamp: Date.now(),
        }),
      );
    }

    if (isDeleteRequest) {
      if (isUpdateSuccess) {
        yield put(
          CallingActions.callingSet({
            deleteCallRoomIds: data.select_all ? ["select_all"] : updateRoomIds,
          }),
        );
      } else {
        yield put(
          CallingActions.callingSet({
            deleteCallRoomIds: null, // Failed
          }),
        );
      }
    }
  } catch (error) {
    console.error(error);
  }
}

export function* getImageIncomingCall(action) {
  try {
    const { avatarId } = action.data;

    let fileName = avatarId + ".png";
    if (!AttachmentUtil.exitsLocalFile(StorageUtil.getCurrentPrefixKey(), avatarId, fileName)) {
      yield getFileFromTus(StorageUtil.getCurrentPrefixKey(), { attachmentId: avatarId, metaData: { fileName } });
    }
  } catch (error) {
    console.error(error);
  }
}
